import React, { useState } from 'react';
import { Info, Scissors, PauseCircle, Flag, Sun, ChevronDown } from 'lucide-react';
import { formatKisa, formatSure, kalanSure } from './formatlayicilar';
import { buildAsamalar, farkMetni } from './asamalar';

// Hafif, slate uyumlu katlanır panel — sonuç alanını sade tutmak için ağır bölümleri sarar.
// (Ortak components/common/CollapsiblePanel navy/gold premium stilde ve varsayılan açık olduğundan
//  bu sade alana ağır kalır; burada kapalı başlayan hafif bir sürüm kullanılır.)
function Katlanir({ baslik, ipucu, defaultOpen = false, children }) {
  const [acik, setAcik] = useState(defaultOpen);
  return (
    <div className="rounded-lg border border-slate-200 bg-white">
      <button
        type="button"
        onClick={() => setAcik((v) => !v)}
        className="flex w-full items-center justify-between gap-2 rounded-lg px-4 py-3 text-left transition-colors hover:bg-slate-50"
      >
        <span className="flex items-baseline gap-2">
          <span className="text-sm font-semibold text-slate-700">{baslik}</span>
          {ipucu ? <span className="text-[11px] text-slate-400">{ipucu}</span> : null}
        </span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${acik ? 'rotate-180' : ''}`}
        />
      </button>
      {acik ? <div className="border-t border-slate-100 p-4 pt-3">{children}</div> : null}
    </div>
  );
}

const kindMeta = {
  baslangic: { Icon: Flag, iconCls: 'bg-sky-50 text-sky-600', railCls: 'border-l-sky-400', muted: false },
  durma: { Icon: PauseCircle, iconCls: 'bg-amber-50 text-amber-600', railCls: 'border-l-amber-400', muted: false },
  'durma-pasif': { Icon: PauseCircle, iconCls: 'bg-slate-100 text-slate-400', railCls: 'border-l-slate-200', muted: true },
  kesme: { Icon: Scissors, iconCls: 'bg-emerald-50 text-emerald-600', railCls: 'border-l-emerald-400', muted: false },
  'kesme-etkisiz': { Icon: Scissors, iconCls: 'bg-slate-100 text-slate-400', railCls: 'border-l-slate-200', muted: true },
};

function BilesenKarti({ etiket, deger, alt, tone = 'slate' }) {
  const tones = { slate: 'text-slate-800', indigo: 'text-indigo-700', violet: 'text-violet-700' };
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">{etiket}</p>
      <p className={`mt-1 text-sm font-bold ${tones[tone]}`}>{deger}</p>
      {alt ? <p className="mt-0.5 text-[11px] text-slate-400">{alt}</p> : null}
    </div>
  );
}

function SonucGosterimi({ sonuc }) {
  const [bilgiAcik, setBilgiAcik] = useState(false);
  if (!sonuc) return null;

  const yas = sonuc.yasGrubuBilgisi;
  const doldu = sonuc.zamanAsimiDoldu;
  const ks = kalanSure(sonuc); // kalan süre: yıl/ay/gün (+ parantezde ham gün) — rapor ile ortak biçim
  const adimlar = sonuc.hesapAdimlari || [];
  const asamalar = buildAsamalar(sonuc);
  const indirimVar = yas && yas.grup !== 'yetiskin';
  const oranMetni = yas?.grup === '12-15' ? '× 1/2' : yas?.grup === '15-18' ? '× 2/3' : null;

  const deltaMetni = (a) => {
    if (a.deltaGun != null) return `+${a.deltaGun} gün`;
    if (a.kind === 'kesme') return 'sıfırlandı';
    if (a.kind === 'baslangic') return formatSure(yas.ayarlanmisSure);
    return '—';
  };

  return (
    <section className="mt-6 rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-5 py-4 sm:px-6">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Ayrıntı</p>
          <h2 className="text-base font-semibold text-slate-800">Hesap ayrıntıları</h2>
        </div>
        <button
          type="button"
          onClick={() => setBilgiAcik((v) => !v)}
          aria-expanded={bilgiAcik}
          title={bilgiAcik ? 'Açıklamayı gizle' : 'Açıklamayı göster'}
          className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border transition-colors ${
            bilgiAcik
              ? 'border-indigo-300 bg-indigo-50 text-indigo-600'
              : 'border-slate-300 text-slate-400 hover:bg-slate-100 hover:text-slate-600'
          }`}
        >
          <Info className="h-4 w-4" />
        </button>
      </div>

      <div className="space-y-6 p-5 sm:p-6">
        {/* Sürenin oluşumu — katlanır */}
        <Katlanir baslik="Sürenin oluşumu" ipucu="66/1 → 66/2 → 67/4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <BilesenKarti etiket="Kanuni süre (66/1)" deger={formatSure(yas.normalSure)} />
            <BilesenKarti
              etiket="Yaş indirimi (66/2)"
              deger={indirimVar ? oranMetni : 'Yok'}
              alt={indirimVar ? yas.etiket : '18 yaş ve üzeri'}
              tone={indirimVar ? 'indigo' : 'slate'}
            />
            <BilesenKarti etiket="Uygulanan süre" deger={formatSure(yas.ayarlanmisSure)} tone="indigo" />
            <BilesenKarti etiket="67/4 uzaması (½)" deger={formatSure(yas.kanuniSureninYarisi)} alt="üst sınır için" tone="violet" />
          </div>
        </Katlanir>

        {/* Aşama aşama geçen süre — katlanır */}
        <Katlanir baslik="Aşama aşama geçen süre" ipucu="olaylar arası gerçekte geçen süre">
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full min-w-[480px] text-sm">
              <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-2.5 font-semibold">Aşama</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Geçen süre</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Toplam gün</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {asamalar.map((a, i) => (
                  <tr key={i} className={a.accent ? (a.doldu ? 'bg-rose-50' : 'bg-emerald-50') : ''}>
                    <td className="px-3 py-2.5 text-slate-700">
                      <span className="text-slate-500">{a.fromLabel}</span>
                      <span className="mx-1 text-slate-300">→</span>
                      <span className="font-medium">{a.toLabel}</span>
                      {a.ref ? <span className="ml-1.5 text-[11px] text-slate-400">{a.ref}</span> : null}
                    </td>
                    <td className="px-3 py-2.5 text-right font-medium tabular-nums text-slate-700">
                      {farkMetni(a.fark)}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-slate-500">{a.fark.gunSayisi} gün</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Katlanir>

        {/* Hesap dökümü — kronolojik defter (katlanır) */}
        <Katlanir baslik="Hesap dökümü — süreç akışı" ipucu="her olaydan sonra işleyen tarih">
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full min-w-[720px] text-sm">
              <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-2.5 font-semibold">#</th>
                  <th className="px-3 py-2.5 font-semibold">Tarih</th>
                  <th className="px-3 py-2.5 font-semibold">Olay</th>
                  <th className="px-3 py-2.5 font-semibold">Etki</th>
                  <th className="px-3 py-2.5 font-semibold">Δ süre</th>
                  <th className="px-3 py-2.5 font-semibold">İşleyen zamanaşımı</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {adimlar.map((a) => {
                  const meta = kindMeta[a.kind] || kindMeta.baslangic;
                  const Icon = meta.Icon;
                  return (
                    <tr key={a.no} className={meta.muted ? 'bg-slate-50/40' : ''}>
                      <td className={`border-l-4 px-3 py-2.5 font-medium tabular-nums text-slate-400 ${meta.railCls}`}>
                        {a.no}
                      </td>
                      <td className="px-3 py-2.5 tabular-nums text-slate-700">{formatKisa(a.tarih)}</td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-2">
                          <span className={`shrink-0 rounded-md p-1 ${meta.iconCls}`}>
                            <Icon className="h-3.5 w-3.5" />
                          </span>
                          <div className="min-w-0">
                            <div className={`font-medium ${meta.muted ? 'text-slate-400' : 'text-slate-800'}`}>
                              {a.baslik}
                            </div>
                            <div className="text-[11px] text-slate-400">
                              {a.tckRef}
                              {a.araStart
                                ? ` · ${formatKisa(a.araStart)} → ${formatKisa(a.araEnd)}`
                                : ''}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className={`px-3 py-2.5 ${meta.muted ? 'text-slate-400' : 'text-slate-600'}`}>{a.etki}</td>
                      <td
                        className={`px-3 py-2.5 font-medium tabular-nums ${
                          a.kind === 'durma' ? 'text-amber-700' : a.kind === 'kesme' ? 'text-emerald-700' : 'text-slate-500'
                        }`}
                      >
                        {deltaMetni(a)}
                      </td>
                      <td className="px-3 py-2.5 font-semibold tabular-nums text-slate-900">
                        {a.bitis ? formatKisa(a.bitis) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="border-t-2 border-slate-200 bg-slate-50/70">
                <tr>
                  <td colSpan={5} className="px-3 py-2.5 text-right font-medium text-slate-600">
                    Olağanüstü üst sınır · TCK 67/4{' '}
                    <span className="text-xs text-slate-400">(uygulanan süre × 1,5{sonuc.toplamDurmaSuresi > 0 ? ' + durma' : ''})</span>
                    {sonuc.capApplied ? (
                      <span className="ml-2 rounded bg-violet-100 px-1.5 py-0.5 text-[11px] font-semibold text-violet-700">
                        sınır uygulandı
                      </span>
                    ) : null}
                  </td>
                  <td className="px-3 py-2.5 font-semibold tabular-nums text-violet-700">
                    {formatKisa(sonuc.olaganustuZamanAsimi)}
                  </td>
                </tr>
                <tr>
                  <td colSpan={5} className="px-3 py-2.5 text-right text-slate-600">
                    <span className="inline-flex items-center gap-1.5">
                      <Sun className="h-3.5 w-3.5 text-red-500" /> Bugün
                      <span className="text-xs text-slate-400">
                        {doldu ? '· süre dolmuş' : `· işliyor, ${ks.ifade} kaldı`}
                      </span>
                    </span>
                  </td>
                  <td className="px-3 py-2.5 tabular-nums text-slate-700">{formatKisa(sonuc.bugun)}</td>
                </tr>
                <tr className={doldu ? 'bg-rose-50' : 'bg-emerald-50'}>
                  <td colSpan={5} className="px-3 py-3 text-right text-sm font-bold text-slate-800">
                    Geçerli zamanaşımı tarihi
                    <span
                      className={`ml-2 rounded-full px-2 py-0.5 text-xs font-semibold ${
                        doldu ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'
                      }`}
                    >
                      {doldu ? 'Dolmuş' : ks.ifade}
                    </span>
                  </td>
                  <td className={`px-3 py-3 font-bold tabular-nums ${doldu ? 'text-rose-700' : 'text-emerald-700'}`}>
                    {formatKisa(sonuc.effective)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          <p className="mt-2 text-[11px] text-slate-400">
            "İşleyen zamanaşımı" sütunu, her olaydan sonra hesaplanan olağan zamanaşımı tarihini gösterir;
            kesilme bu tarihi sıfırlar, durma ileri öteler.
          </p>
        </Katlanir>

        {/* Bilgilendirme — "i" ile aç/kapa */}
        {bilgiAcik ? (
          <div className="flex items-start gap-2.5 rounded-lg border border-slate-200 bg-slate-50/60 p-4">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
            <p className="text-sm text-slate-600">
              Olağan zamanaşımı, son kesilmeden itibaren uygulanan sürenin (66/1 → 66/2) dolmasıyla gerçekleşir.
              Olağanüstü üst sınır (67/4), suç tarihinden itibaren uygulanan süre + durma + aynı sürenin en fazla
              yarısı ile sınırlıdır; en az bir kesilme varsa olağan tarih bu üst tarihi geçemez. Durmanın (67/1)
              üst sınırı yoktur ve her iki tarihi de öteler.
            </p>
          </div>
        ) : null}
      </div>
    </section>
  );
}

export default SonucGosterimi;
